import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DAY_MS, nextMelbourneDailyRunAtOrAfter } from '../../shared/lifecycle';
import type {
  AdminProjectOverviewResponse,
  AdminStorageResponse,
  ReviewProjectSummary,
} from '../../shared/types';
import Chip from '../components/Chip';
import Eyebrow from '../components/Eyebrow';
import { showToast } from '../components/Toaster';
import {
  AdminFooter,
  AdminShell,
  FieldLabel,
  SectionHeading,
  StatePanel,
  StatusChip,
  cardClass,
  formatBytes,
  formatDate,
  inputClass,
  primaryButtonGlowClass,
  quietButtonClass,
  secondaryButtonClass,
} from '../components/admin/AdminUi';
import RowMenu from '../components/admin/RowMenu';
import { adminApi, ApiError } from '../lib/api';

type StorageState =
  | { kind: 'loading' }
  | { kind: 'ready'; value: AdminStorageResponse }
  | { kind: 'unavailable' }
  | { kind: 'error' };

type DashboardTab = 'projects' | 'trash';

export default function AdminDashboardPage() {
  const [projects, setProjects] = useState<ReviewProjectSummary[] | null>(null);
  const [trash, setTrash] = useState<ReviewProjectSummary[] | null>(null);
  const [projectTotal, setProjectTotal] = useState<number | null>(null);
  const [trashTotal, setTrashTotal] = useState<number | null>(null);
  const [projectCursor, setProjectCursor] = useState<string | null>(null);
  const [trashCursor, setTrashCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState<DashboardTab | null>(null);
  const [overviews, setOverviews] = useState<Record<string, AdminProjectOverviewResponse>>({});
  const [storage, setStorage] = useState<StorageState>({ kind: 'loading' });
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState<DashboardTab>('projects');
  const [deleteForeverProject, setDeleteForeverProject] = useState<ReviewProjectSummary | null>(null);
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestSequence.current;
    setError(null);
    try {
      const [response, trashResponse] = await Promise.all([
        adminApi.listProjects({ limit: 100 }),
        adminApi.listProjects({ status: 'deleted', limit: 100 }),
      ]);
      if (request !== requestSequence.current) return;
      setProjects(response.projects);
      setTrash(trashResponse.projects);
      setProjectTotal(response.total);
      setTrashTotal(trashResponse.total);
      setProjectCursor(response.next_cursor);
      setTrashCursor(trashResponse.next_cursor);
      const results = await Promise.all(
        response.projects.map(async (project) => {
          try {
            return [project.id, await adminApi.overview(project.id)] as const;
          } catch {
            return null;
          }
        }),
      );
      if (request !== requestSequence.current) return;
      setOverviews(
        Object.fromEntries(results.filter((result): result is NonNullable<typeof result> => result !== null)),
      );
    } catch (caught) {
      if (request !== requestSequence.current) return;
      setError(caught instanceof ApiError ? caught.message : 'We could not load your projects. Mind refreshing?');
      setProjects([]);
      setTrash([]);
      setProjectTotal(0);
      setTrashTotal(0);
      setProjectCursor(null);
      setTrashCursor(null);
    }
  }, []);

  const loadMore = useCallback(async (target: DashboardTab) => {
    const cursor = target === 'projects' ? projectCursor : trashCursor;
    if (!cursor) return;
    const request = ++requestSequence.current;
    setLoadingMore(target);
    try {
      const response = await adminApi.listProjects({
        status: target === 'trash' ? 'deleted' : undefined,
        cursor,
        limit: 100,
      });
      if (request !== requestSequence.current) return;
      if (target === 'projects') {
        setProjects((current) => [...(current ?? []), ...response.projects]);
        setProjectCursor(response.next_cursor);
        setProjectTotal(response.total);
        const results = await Promise.all(
          response.projects.map(async (project) => {
            try {
              return [project.id, await adminApi.overview(project.id)] as const;
            } catch {
              return null;
            }
          }),
        );
        if (request !== requestSequence.current) return;
        setOverviews((current) => ({
          ...current,
          ...Object.fromEntries(
            results.filter((result): result is NonNullable<typeof result> => result !== null),
          ),
        }));
      } else {
        setTrash((current) => [...(current ?? []), ...response.projects]);
        setTrashCursor(response.next_cursor);
        setTrashTotal(response.total);
      }
    } catch {
      if (request === requestSequence.current) {
        showToast('error', 'We could not load more projects. Mind trying again?');
      }
    } finally {
      if (request === requestSequence.current) setLoadingMore(null);
    }
  }, [projectCursor, trashCursor]);

  useEffect(() => {
    void load();
    adminApi
      .storage()
      .then((value) => setStorage({ kind: 'ready', value }))
      .catch((caught) => {
        setStorage(caught instanceof ApiError && caught.status === 501 ? { kind: 'unavailable' } : { kind: 'error' });
      });
  }, [load]);

  const visibleProjects = tab === 'projects' ? projects : trash;
  const visibleNextCursor = tab === 'projects' ? projectCursor : trashCursor;

  return (
    <AdminShell>
      <div className="mx-auto max-w-6xl px-5 py-12 md:px-10 md:py-16">
        <header className="flex flex-wrap items-end justify-between gap-8">
          <div className="rise">
            <SectionHeading
              level="page"
              eyebrow="Projects"
              title="Review projects"
              description="Set up interviews, publish private links, and keep an eye on client decisions."
            />
          </div>
          <button type="button" className={`${primaryButtonGlowClass} rise`} style={{ animationDelay: '260ms' }} onClick={() => setShowNew(true)}>
            New project
          </button>
        </header>

        <main className="mt-6">
          <div className="tabs rise-subtle" aria-label="Project lists" style={{ animationDelay: '320ms' }}>
            <button
              type="button"
              className={`tab ${tab === 'projects' ? 'tab-active' : ''}`}
              aria-pressed={tab === 'projects'}
              onClick={() => setTab('projects')}
            >
              Projects · {projectTotal ?? '…'}
            </button>
            <button
              type="button"
              className={`tab ${tab === 'trash' ? 'tab-active' : ''}`}
              aria-pressed={tab === 'trash'}
              onClick={() => setTab('trash')}
            >
              Trash · {trashTotal ?? '…'}
            </button>
          </div>
          <section className="mt-6" aria-labelledby="projects-heading">
            <h2 id="projects-heading" className="sr-only">{tab === 'projects' ? 'Projects' : 'Trash'}</h2>
            {visibleProjects === null ? (
              <StatePanel eyebrow="Loading" title="Gathering your projects">
                <p>We are checking the latest review activity now.</p>
              </StatePanel>
            ) : error ? (
              <StatePanel eyebrow="Could not load" title="Your projects are still safe" tone="error">
                <p>{error}</p>
                <button type="button" className={`${secondaryButtonClass} mt-5`} onClick={() => void load()}>
                  Try again
                </button>
              </StatePanel>
            ) : visibleProjects.length === 0 && tab === 'trash' ? (
              <div className="card-dashed rise-subtle px-8 py-14" style={{ animationDelay: '380ms' }}>
                <Eyebrow>Trash</Eyebrow>
                <h2 className="title-md mt-3">Trash is empty</h2>
                <p className="mx-auto mt-2 max-w-md text-[15px] text-muted">
                  Deleted projects land here for 30 days before they're purged.
                </p>
              </div>
            ) : visibleProjects.length === 0 ? (
              <div className="card-dashed rise-subtle px-8 py-14" style={{ animationDelay: '380ms' }}>
                <Eyebrow>No projects</Eyebrow>
                <h2 className="title-md mt-3">Start your first client review</h2>
                <p className="mx-auto mt-2 max-w-md text-[15px] text-muted">Add a project, then set up each interview and its transcript.</p>
                <button type="button" className={`${secondaryButtonClass} mt-6`} onClick={() => setShowNew(true)}>Set up a project</button>
              </div>
            ) : (
              <ul className="grid gap-4">
                {visibleProjects.map((project, index) =>
                  tab === 'projects' ? (
                    <ProjectCard key={project.id} project={project} overview={overviews[project.id]} index={index} />
                  ) : (
                    <TrashProjectCard
                      key={project.id}
                      project={project}
                      index={index}
                      onRestored={load}
                      onDeleteForever={() => setDeleteForeverProject(project)}
                    />
                  ),
                )}
              </ul>
            )}
            {visibleProjects !== null && visibleProjects.length > 0 && visibleNextCursor ? (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  className={quietButtonClass}
                  onClick={() => void loadMore(tab)}
                  disabled={loadingMore !== null}
                >
                  {loadingMore === tab ? 'Loading more' : 'Load more'}
                </button>
              </div>
            ) : null}
          </section>

          {tab === 'projects' ? (
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <StoragePanel state={storage} />
              <section className={`${cardClass} rise-subtle p-7`} style={{ animationDelay: '560ms' }}>
                <Eyebrow accent>Operations</Eyebrow>
                <p className="mt-5 text-sm text-muted">Check the error log if a review or notification behaves unexpectedly.</p>
                <Link to="/admin/security" className={`${quietButtonClass} mt-3 -ml-4`}>View security →</Link>
              </section>
            </div>
          ) : null}
        </main>
      </div>
      <AdminFooter path="ss-review / projects" />
      {showNew ? <NewProjectDialog onClose={() => setShowNew(false)} /> : null}
      {deleteForeverProject ? (
        <DeleteForeverDialog
          project={deleteForeverProject}
          onClose={() => setDeleteForeverProject(null)}
          onDeleted={async () => {
            setDeleteForeverProject(null);
            await load();
          }}
        />
      ) : null}
    </AdminShell>
  );
}

function ProjectCard({
  project,
  overview,
  index,
}: {
  project: ReviewProjectSummary;
  overview: AdminProjectOverviewResponse | undefined;
  index: number;
}) {
  const counts = overview?.counts;
  return (
    <li className="card-row rise-subtle" style={{ animationDelay: `${380 + Math.min(index, 4) * 60}ms` }}>
      <Link to={`/admin/projects/${project.id}`} className="block p-5 text-fg no-underline md:px-6 md:py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="title-md">{project.title}</h2>
            <p className="eyebrow mt-2">
              {project.client_name} ·{' '}
              {project.expiry_at === null ? 'No expiry' : `expires ${formatDate(project.expiry_at)}`}
            </p>
          </div>
          <StatusChip status={project.status} />
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <DecisionChip label="Use" count={counts?.use} tone="success" />
          <DecisionChip label="Maybe" count={counts?.maybe} tone="warning" />
          <DecisionChip label="Remove" count={counts?.remove} tone="error" />
          <Chip>{project.asset_count} {project.asset_count === 1 ? 'interview' : 'interviews'}</Chip>
          <Chip>{project.reviewer_count} {project.reviewer_count === 1 ? 'reviewer' : 'reviewers'}</Chip>
          <Chip>{formatBytes(project.storage_bytes)}</Chip>
        </div>
      </Link>
    </li>
  );
}

function DecisionChip({
  label,
  count,
  tone,
}: {
  label: string;
  count: number | undefined;
  tone: 'success' | 'warning' | 'error' | 'plain';
}) {
  return <Chip tone={tone}>{label} {count ?? '...'}</Chip>;
}

function TrashProjectCard({
  project,
  index,
  onRestored,
  onDeleteForever,
}: {
  project: ReviewProjectSummary;
  index: number;
  onRestored: () => Promise<void>;
  onDeleteForever: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const purgeAt = project.deleted_at === null
    ? null
    : nextMelbourneDailyRunAtOrAfter(project.deleted_at + 30 * DAY_MS);

  async function restore() {
    setBusy(true);
    setError(null);
    try {
      await adminApi.restoreProject(project.id);
      await onRestored();
      showToast('success', 'Project restored.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not restore that project. Mind trying again?');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="card-row group relative rise-subtle p-5 md:px-6 md:py-5" style={{ animationDelay: `${380 + Math.min(index, 4) * 60}ms` }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="title-md">{project.title}</h2>
          <p className="eyebrow mt-2">
            Deleted {formatDate(project.deleted_at)} · purges {formatDate(purgeAt)}
          </p>
          {busy ? <p className="mt-2 text-[13px] text-muted" role="status">Restoring project…</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StatusChip status="deleted" />
          <RowMenu
            label={`Actions for ${project.title}`}
            disabled={busy}
            items={[
              {
                label: busy ? 'Restoring project' : 'Restore project',
                onClick: () => void restore(),
                disabled: busy,
              },
              {
                label: 'Delete forever…',
                onClick: onDeleteForever,
                destructive: true,
                disabled: busy,
              },
            ]}
          />
        </div>
      </div>
      {error ? <p className="mt-4 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">{error}</p> : null}
    </li>
  );
}

function DeleteForeverDialog({
  project,
  onClose,
  onDeleted,
}: {
  project: ReviewProjectSummary;
  onClose: () => void;
  onDeleted: () => Promise<void>;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (confirmation !== project.title) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.hardDeleteProject(project.id);
      await onDeleted();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not permanently delete that project. Mind trying again?');
      setBusy(false);
    }
  }

  return (
    <div
      className="app-scrim fade-in fixed inset-0 z-50 grid place-items-center overflow-y-auto p-5"
      role="presentation"
      onMouseDown={busy ? undefined : onClose}
    >
      <form
        className={`${cardClass} scale-in my-8 w-full max-w-xl p-8 shadow-[var(--shadow-ambient-lg)]`}
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-forever-title"
      >
        <Eyebrow tone="error">Delete forever</Eyebrow>
        <h2 id="delete-forever-title" className="title-lg mt-3">Permanently delete {project.title}?</h2>
        <p className="mt-3 text-sm text-muted">
          This permanently removes the project, its interviews, transcripts and every decision on them. This cannot be undone.
        </p>
        <p className="mt-5 text-sm text-muted">
          Type <strong className="text-fg">{project.title}</strong> to confirm.
        </p>
        <label className="mt-4 block">
          <FieldLabel>Project title</FieldLabel>
          <input
            className={inputClass}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoFocus
          />
        </label>
        {error ? <p className="mt-4 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">{error}</p> : null}
        <div className="mt-7 flex flex-wrap justify-end gap-3">
          <button type="button" className={quietButtonClass} onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="submit"
            className={`${secondaryButtonClass} border-error/50 text-error hover:border-error`}
            disabled={busy || confirmation !== project.title}
          >
            {busy ? 'Deleting forever' : 'Delete forever'}
          </button>
        </div>
      </form>
    </div>
  );
}

function StoragePanel({ state }: { state: StorageState }) {
  if (state.kind === 'loading') {
    return <StatePanel eyebrow="Storage" title="Checking usage"><p>One moment while we count the media.</p></StatePanel>;
  }
  if (state.kind === 'unavailable') {
    return (
      <StatePanel eyebrow="Storage" title="Storage totals are not available yet" tone="tint">
        <p>The Phase 1B media package will add this meter. Project management is ready now.</p>
      </StatePanel>
    );
  }
  if (state.kind === 'error') {
    return (
      <StatePanel eyebrow="Storage" title="We could not check storage" tone="error">
        <p>Nothing needs your attention yet. Try the storage page again shortly.</p>
      </StatePanel>
    );
  }
  const usedPercent = state.value.ceiling_bytes > 0
    ? Math.min(100, (state.value.used_bytes / state.value.ceiling_bytes) * 100)
    : 0;
  return (
    <section className={`${cardClass} rise-subtle p-7`} style={{ animationDelay: '500ms' }}>
      <Eyebrow accent>Storage</Eyebrow>
      <div className="mt-5 flex items-baseline gap-2">
        <p className="font-display text-4xl font-semibold leading-none">{formatBytes(state.value.used_bytes)}</p>
        <p className="text-sm text-muted">of {formatBytes(state.value.ceiling_bytes)}</p>
      </div>
      <div className="meter mt-5" aria-label={`${usedPercent.toFixed(0)} per cent used`}>
        <div className="meter-fill" style={{ width: `${usedPercent}%` }} />
      </div>
      <p className="mt-3 text-xs text-faint">Includes {formatBytes(state.value.trash_bytes)} in Trash</p>
      <Link to="/admin/storage" className={`${quietButtonClass} mt-3 -ml-4`}>View storage →</Link>
    </section>
  );
}

function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const defaultDate = new Date(Date.now() + 60 * 86_400_000).toISOString().slice(0, 10);
  const [title, setTitle] = useState('');
  const [clientName, setClientName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [expiry, setExpiry] = useState(defaultDate);
  const [indefinite, setIndefinite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await adminApi.createProject({
        title,
        client_name: clientName,
        instructions: instructions.trim() || null,
        expiry_at: indefinite ? null : new Date(`${expiry}T23:59:59`).getTime(),
      });
      navigate(`/admin/projects/${response.project.id}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not create the project. Mind trying again?');
      setBusy(false);
    }
  }

  return (
    <div className="app-scrim fade-in fixed inset-0 z-50 grid place-items-center overflow-y-auto p-5" role="presentation" onMouseDown={onClose}>
      <form
        className={`${cardClass} scale-in my-8 w-full max-w-xl p-8 shadow-[var(--shadow-ambient-lg)]`}
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
        aria-labelledby="new-project-title"
      >
        <Eyebrow accent>New project</Eyebrow>
        <h2 id="new-project-title" className="title-lg mt-3">Set up the review</h2>
        <p className="mt-2 text-sm text-muted">Start with the client-facing details. Assets come next.</p>
        <label className="mt-6 block">
          <FieldLabel>Project title</FieldLabel>
          <input className={inputClass} value={title} onChange={(event) => setTitle(event.target.value)} required autoFocus />
        </label>
        <label className="mt-4 block">
          <FieldLabel>Client name</FieldLabel>
          <input className={inputClass} value={clientName} onChange={(event) => setClientName(event.target.value)} required />
        </label>
        <label className="mt-4 block">
          <FieldLabel>Reviewer instructions</FieldLabel>
          <textarea className={`${inputClass} min-h-28 resize-y`} value={instructions} onChange={(event) => setInstructions(event.target.value)} />
        </label>
        <label className="mt-4 block">
          <FieldLabel>Proposed expiry</FieldLabel>
          <input className={inputClass} type="date" value={expiry} onChange={(event) => setExpiry(event.target.value)} disabled={indefinite} required={!indefinite} />
        </label>
        <label className="mt-4 flex min-h-11 items-center gap-3 text-sm text-muted">
          <input type="checkbox" checked={indefinite} onChange={(event) => setIndefinite(event.target.checked)} className="h-5 w-5 accent-accent" />
          Keep this project indefinitely
        </label>
        {error ? <p className="mt-4 rounded-input bg-error-tint p-3 text-sm text-error" role="alert">{error}</p> : null}
        <div className="mt-7 flex flex-wrap justify-end gap-3">
          <button type="button" className={quietButtonClass} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className={primaryButtonGlowClass} disabled={busy}>{busy ? 'Creating project' : 'Create project'}</button>
        </div>
      </form>
    </div>
  );
}
